import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import * as React from 'react';

import { cn } from '../../lib/utils.ts';

type RadioGroupProps = {
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    className?: string;
    children?: React.ReactNode;
    disabled?: boolean;
};

const RadioGroup = React.forwardRef<React.ElementRef<typeof ToggleGroupPrimitive.Root>, RadioGroupProps>(
    ({ className, onValueChange, ...props }, ref) => {
        return (
            <ToggleGroupPrimitive.Root
                type="single"
                className={cn('divider-neutral-200 inline-flex flex-row', className)}
                onValueChange={(value) => {
                    if (!value) {
                        return;
                    }

                    onValueChange?.(value);
                }}
                {...props}
                ref={ref}
            />
        );
    },
);
RadioGroup.displayName = 'RadioGroup';

type RadioGroupItemProps = {
    icon?: React.ComponentType<{ className?: string }>;
} & React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>;

const RadioGroupItem = React.forwardRef<
    React.ElementRef<typeof ToggleGroupPrimitive.Item>,
    RadioGroupItemProps
>(({ className, children, icon: Icon, ...props }, ref) => {
    return (
        <ToggleGroupPrimitive.Item
            ref={ref}
            unselectable="off"
            className={cn(
                'border-primary text-primary focus-visible:ring-ring flex h-8 items-center justify-center border-y border-r',
                'border-neutral-200 bg-white px-3 first:rounded-l-lg first:border-l',
                'last:rounded-r-lg focus:outline-hidden focus-visible:ring-1  disabled:cursor-not-allowed disabled:opacity-50 text-sm',
                'data-[state="on"]:bg-blue-100 data-[state="on"]:text-blue-600',
                'flex flex-row items-center shrink-0 gap-1',
                className,
            )}
            {...props}
        >
            {Icon && <Icon className="h-4 w-4" />}
            <span className="whitespace-nowrap">{children}</span>
        </ToggleGroupPrimitive.Item>
    );
});
RadioGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
